import { createApi } from "@reduxjs/toolkit/query/react";

import {
  VendorStatsApiResponse,
  VendorStatsData,
  VendorDetailsResponse,
  PoListApiResponse,
  PurchaseOrder,
  IVendorList,
} from "../../utils/types";
import { baseQueryWithLogout } from "../baseQuery";

export const vendorApi = createApi({
  reducerPath: "vendorApi",
  baseQuery: baseQueryWithLogout,
  endpoints: (builder) => ({
    getEmployeeDetails: builder.query<VendorStatsData, void>({
      query: () => ({
        url: "/f4_employeeset",
        method: "GET",
      }),
      transformResponse: (response: VendorStatsApiResponse) => {
        if (response && response["d"].results) {
          return response["d"].results;
        }
        throw new Error(response.message || "Failed to fetch vendor stats");
      },
    }),
    getVendorStats: builder.query<VendorStatsData, void>({
      query: () => ({
        url: "/vendor-stats",
        method: "GET",
      }),
      transformResponse: (response: VendorStatsApiResponse) => {
        if (response && response.success && response.data) {
          return response.data;
        }
        throw new Error(response.message || "Failed to fetch vendor stats");
      },
    }),
    getVendorDetails: builder.query<VendorDetailsResponse["data"], void>({
      query: () => ({
        url: "/get_vendor_details",
        method: "GET",
      }),
      transformResponse: (response: VendorDetailsResponse) => {
        if (response.success && response.data) {
          return response.data;
        }
        throw new Error(response.message || "Failed to fetch vendor details");
      },
    }),
    getVendorDetailsWithId: builder.query<any, void>({
      query: (vendorId: any) => ({
        url: "/api_vendor_info_odata?vendor_code=" + vendorId,
        method: "GET",
      }),
      transformResponse: (response: VendorDetailsResponse) => {
        if (response.success && response.message) {
          return response.message;
        }
        throw new Error(response.message || "Failed to fetch vendor details");
      },
    }),
    getPoList: builder.query<PurchaseOrder[], string>({
      query: (vendorCode) => ({
        url: "/api_po_list",
        method: "GET",
        params: { vendor_code: vendorCode },
      }),
      transformResponse: (response: PoListApiResponse) => {
        if (response && response.success) {
          return response.po_list;
        }
        throw new Error(response.message || "Failed to load PO list");
      },
    }),
    getPoListWithProcurement: builder.mutation({
      query: (param: any) => ({
        url: "/api_po_list",
        method: "GET",
        params: { vendor_code: param.vendorCode, procurement_type: param.procurementType },
      }),
      transformResponse: (response: PoListApiResponse) => {
        if (response && response.success) {
          return response.po_list;
        }
        throw new Error(response.message || "Failed to load PO list");
      },
    }),
    getAllVendorPoList: builder.query<IVendorList[], any>({
      query: (type: any) => {
        return {
          url: "/api_vendor_info?module=" + type,
          method: "GET",
        };
      },
      transformResponse: (response: any) => {
        if (response && response.success) {
          return response.vendors;
        }
        throw new Error(response.message || "Failed to load PO list");
      },
    }),
    getVendorListWithSearch: builder.query<any, any>({
      query: (payload) => {
        return {
          url: "/api_vendor_info?search=" + payload,
          method: "GET",
        };
      },
      transformResponse: (response: any) => {
        if (response && response.success) {
          return response.vendors;
        }
        throw new Error(response.message || "Failed to load PO list");
      },
    }),
    getVendorDownloadTds: builder.mutation<any, any>({
      query: (payload) => {
        return {
          url: "/view-tds?" + payload,
          method: "GET",
        };
      },
    }),
    getDropdownOptions: builder.query<any, string>({
      query: (key) => {
        return {
          url: `/dropdown?key=${key}`,
          method: "GET",
        };
      },
      transformResponse: (response: any) => {
        if (response) {
          return response;
        }
        throw new Error(response.message || "Failed to load PO list");
      },
    }),
    getFiEmpLocation: builder.query<any, any>({
      query: (key) => {
        return {
          url: `/FI_employee_locations`,
          method: "GET",
        };
      },
      transformResponse: (response: any) => {
        if (response) {
          return response;
        }
        throw new Error(response.message || "Failed to load PO list");
      },
    }),
    getFiEmpDepartment: builder.query({
      query: (key) => {
        return {
          url: 'FI_employee_departments?location_code=' + key,
          method: "GET",
        };
      },
      transformResponse: (response: any) => {
        if (response) {
          return response;
        }
        throw new Error(response.message || "Failed to load PO list");
      },
    }),
    getFiEmpHod: builder.mutation({
      query: (key) => {
        return {
          url: '/FI_employee_hod?' + key,
          method: "GET",
        };
      },

    }),
    getFetchUserManual: builder.mutation({
      query: () => {
        return {
          url: '/fetch/user_manual',
          method: "GET",
        };
      },
    }),
    getFileContant: builder.mutation({
      query: (playload) => {
        return {
          url: '/file_content?' + playload,
          method: "GET",
        };
      },
    }),
    getApproverPageDetails: builder.mutation({
      // 'detailsPayload' will be the object you pass with po_contract, etc.
      query: (detailsPayload) => {
        // IMPORTANT: Convert the JavaScript object to the x-www-form-urlencoded format
        const body = new URLSearchParams();
        for (const key in detailsPayload) {
          if (detailsPayload[key]) {
            // Only append keys that have a value
            body.append(key, detailsPayload[key]);
          }
        }

        return {
          url: "/approver_page",
          method: "POST",
          body: body, // Use the formatted body
          // The 'Content-Type' header is set to 'x-www-form-urlencoded' automatically by the browser when you pass a URLSearchParams object.
        };
      },
    }),
    getSelectedH_TList: builder.mutation({
      query: (detailsPayload) => {
        const body = new URLSearchParams();
        for (const key in detailsPayload) {
          if (detailsPayload[key]) {
            body.append(key, detailsPayload[key]);
          }
        }
        return {
          url: "/ht_grn",
          method: "POST",
          body: body,
        };
      },
    }),
    getApiAssignTo: builder.mutation({
      query: (playload) => {
        return {
          url: '/api/assign_to/' + playload.bts,
          method: "PUT",
          body: playload.body
        };
      },
    }),
    getTDSAllAttachments: builder.mutation({
      query: (playload) => {
        return {
          url: '/api/tds/get-view?' + playload,
          method: "GET"
        };
      },
    }),
    getCheckLoginAccess: builder.mutation({
      query: (playload) => {
        return {
          url: '/check_login_access',
          method: "POST",
          body: playload
        };
      },
    }),
    getServerInfo: builder.query({
      query: () => {
        return {
          url: '/server-info',
          method: "GET",
        };
      },
    }),
  }),
});

export const {
  useGetServerInfoQuery,
  useGetCheckLoginAccessMutation,
  useGetTDSAllAttachmentsMutation,
  useGetVendorListWithSearchQuery,
  useGetApiAssignToMutation,
  useGetFileContantMutation,
  useGetFetchUserManualMutation,
  useGetSelectedH_TListMutation,
  useGetFiEmpHodMutation,
  useGetFiEmpDepartmentQuery,
  useGetFiEmpLocationQuery,
  useGetVendorDownloadTdsMutation,
  useGetPoListWithProcurementMutation,
  useGetEmployeeDetailsQuery,
  useGetVendorStatsQuery,
  useGetVendorDetailsQuery,
  useGetPoListQuery,
  useGetAllVendorPoListQuery,
  useGetApproverPageDetailsMutation,
  useGetDropdownOptionsQuery,
  useGetVendorDetailsWithIdQuery,
} = vendorApi;
